import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';

const PLANS = [
  { name: 'Starter',      branches: 1  },
  { name: 'Business',     branches: 3  },
  { name: 'Professional', branches: 5  },
  { name: 'Enterprise',   branches: 999 },
];

const ADMIN_ID = 'cac6dfc1-bced-48fa-b920-3d17147bfd06';

export default function AdminPage({ session, onBack }) {
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(null);
  const [msg, setMsg]         = useState('');

  useEffect(() => {
    loadUsers();
  }, []);

  async function loadUsers() {
    setLoading(true);
    const { data, error } = await supabase
      .from('user_settings')
      .select('*')
      .order('created_at', { ascending: false });
    if (!error) setUsers(data || []);
    setLoading(false);
  }

  async function updatePlan(userId, planName, branchLimit) {
    setSaving(userId);
    setMsg('');
    const { error } = await supabase
      .from('user_settings')
      .update({ plan_name: planName, branch_limit: branchLimit, updated_at: new Date().toISOString() })
      .eq('user_id', userId);
    if (!error) {
      setMsg('✅ Plan updated successfully!');
      loadUsers();
    } else {
      setMsg('❌ Error updating plan.');
    }
    setSaving(null);
  }

  if (session.user.id !== ADMIN_ID) {
    return (
      <div style={S.page}>
        <div style={S.card}>
          <h2 style={{ color: '#dc2626' }}>⛔ Access Denied</h2>
          <p>You do not have permission to view this page.</p>
          <button onClick={onBack} style={S.btnBack}>← Back to Dashboard</button>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>🛡️ Admin Panel</h1>
          <p style={S.subtitle}>Manage user plans and branch limits</p>
        </div>
        <button onClick={onBack} style={S.btnBack}>← Back to Dashboard</button>
      </div>

      {msg && <div style={S.msg}>{msg}</div>}

      {loading ? (
        <div style={S.loading}>Loading users...</div>
      ) : (
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              <tr>
                {['Email', 'Plan', 'Branch Limit', 'Actions', 'Since'].map(h => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => (
                <tr key={u.id} style={{ background: i % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                  <td style={S.td}>{u.email}</td>
                  <td style={S.td}>
                    <span style={{
                      ...S.badge,
                      background: u.plan_name === 'Admin' ? '#1e1b4b' :
                                  u.plan_name === 'Enterprise' ? '#064e3b' :
                                  u.plan_name === 'Professional' ? '#1e3a5f' :
                                  u.plan_name === 'Business' ? '#92400e' : '#374151',
                    }}>
                      {u.plan_name}
                    </span>
                  </td>
                  <td style={{ ...S.td, textAlign: 'center', fontWeight: 700, color: '#4f46e5' }}>
                    {u.branch_limit === 999 ? '∞ Unlimited' : u.branch_limit}
                  </td>
                  <td style={S.td}>
                    {u.user_id === ADMIN_ID ? (
                      <span style={{ color: '#94a3b8', fontSize: 13 }}>— Admin Account —</span>
                    ) : (
                      <div style={S.planRow}>
                        {PLANS.map(p => (
                          <button
                            key={p.name}
                            onClick={() => updatePlan(u.user_id, p.name, p.branches)}
                            disabled={saving === u.user_id}
                            style={{
                              ...S.planBtn,
                              background: u.plan_name === p.name ? '#4f46e5' : '#e2e8f0',
                              color: u.plan_name === p.name ? '#ffffff' : '#374151',
                            }}
                          >
                            {p.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </td>
                  <td style={{ ...S.td, color: '#94a3b8', fontSize: 13 }}>
                    {new Date(u.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {users.length === 0 && (
            <div style={S.empty}>No users found. Users appear here after their first login.</div>
          )}
        </div>
      )}
    </div>
  );
}

const S = {
  page: { padding: '32px', fontFamily: "'Segoe UI', Arial, sans-serif", maxWidth: 1100, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32 },
  title: { fontSize: 28, fontWeight: 800, color: '#0f172a', margin: 0 },
  subtitle: { color: '#64748b', margin: '4px 0 0', fontSize: 14 },
  btnBack: { background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontSize: 14, color: '#374151', fontWeight: 600 },
  msg: { background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '12px 16px', marginBottom: 20, fontSize: 14, color: '#065f46' },
  loading: { textAlign: 'center', padding: 60, color: '#94a3b8' },
  tableWrap: { background: '#ffffff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { background: '#0f172a', color: '#ffffff', padding: '12px 16px', textAlign: 'left', fontSize: 13, fontWeight: 600 },
  td: { padding: '12px 16px', fontSize: 14, color: '#1e293b', borderBottom: '1px solid #f1f5f9' },
  badge: { color: '#ffffff', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 600 },
  planRow: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  planBtn: { border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  empty: { textAlign: 'center', padding: 40, color: '#94a3b8' },
};
